import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Database, BarChart3, AlertTriangle, Link2, GraduationCap,
  Network, Lightbulb, RefreshCw, Lock, Eye, EyeOff, Search,
  ArrowLeft, Download, Filter, ChevronDown, ChevronUp, 
  CheckCircle, XCircle, AlertCircle, Info, BookOpen, 
  Building2, MapPin, Globe, Users, Briefcase, FileText, Target,
  GitCompare, Save, Trash2, ArrowRight, TrendingUp, TrendingDown, Minus, Clock
} from 'lucide-react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, Treemap, RadialBarChart, RadialBar
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_PASSWORD = "declic2025";

const COLORS = ['#A4187A', '#FB6823', '#4CAF50', '#2196F3', '#E91E63', '#9C27B0', '#00BCD4', '#FFC107', '#795548', '#607D8B'];
const CATEGORY_COLORS = {
  highly_accessible: '#4CAF50',
  moderately_accessible: '#FFC107',
  low_accessibility: '#FF9800',
  critical: '#F44336'
};

export default function AuditDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('methodology');
  const [lang, setLang] = useState('fr');
  const [exporting, setExporting] = useState(false);
  
  // Data states
  const [careerMapping, setCareerMapping] = useState(null);
  const [questionImpact, setQuestionImpact] = useState(null);
  const [biasDetection, setBiasDetection] = useState(null);
  const [missingLinks, setMissingLinks] = useState(null);
  const [educationData, setEducationData] = useState(null);
  const [visualization, setVisualization] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [accessibilityData, setAccessibilityData] = useState(null);
  
  // Snapshot/Comparison states
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [snapshotName, setSnapshotName] = useState('');
  const [snapshotDescription, setSnapshotDescription] = useState('');
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [comparingSnapshot, setComparingSnapshot] = useState(false);
  const [algorithmComparison, setAlgorithmComparison] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  
  // Sorting state for career mapping table
  const [sortConfig, setSortConfig] = useState({ key: 'suggested_count', direction: 'desc' });

  // Handle sort for career mapping table
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  
  // Sort careers based on current config
  const getSortedCareers = (careers) => {
    if (!careers || !Array.isArray(careers)) return [];
    
    return [...careers].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'domains':
          aVal = a.domain_labels?.join(',') || '';
          bVal = b.domain_labels?.join(',') || '';
          break;
        case 'suggested_count':
          aVal = a.suggested_count || 0;
          bVal = b.suggested_count || 0;
          break;
        case 'selected_count':
          aVal = a.selected_count || 0;
          bVal = b.selected_count || 0;
          break;
        case 'selection_rate':
          aVal = a.selection_rate || 0;
          bVal = b.selection_rate || 0;
          break;
        case 'associated_questions_count':
          aVal = a.associated_questions_count || 0;
          bVal = b.associated_questions_count || 0;
          break;
        case 'formations_count':
          aVal = a.formations_count || 0;
          bVal = b.formations_count || 0;
          break;
        default:
          aVal = a[sortConfig.key] || 0;
          bVal = b[sortConfig.key] || 0;
      }
      
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };
  
  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-300 ml-1 text-xs">↕</span>;
    }
    return (
      <span className="text-[#A4187A] ml-1 text-xs">
        {sortConfig.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      loadData();
    } else {
      setAuthError(lang === 'fr' ? 'Mot de passe incorrect' : 'Onjuist wachtwoord');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [careers, questions, bias, links, education, viz, recs, accessibility] = await Promise.all([
        axios.get(`${API}/audit/career-mapping?lang=${lang}`),
        axios.get(`${API}/audit/question-impact?lang=${lang}`),
        axios.get(`${API}/audit/bias-detection?lang=${lang}`),
        axios.get(`${API}/audit/missing-links?lang=${lang}`),
        axios.get(`${API}/audit/education-data?lang=${lang}`),
        axios.get(`${API}/audit/visualization?lang=${lang}`),
        axios.get(`${API}/audit/recommendations?lang=${lang}`),
        axios.get(`${API}/audit/accessibility?lang=${lang}`)
      ]);
      
      setCareerMapping(careers.data);
      setQuestionImpact(questions.data);
      setBiasDetection(bias.data);
      setMissingLinks(links.data);
      setEducationData(education.data);
      setVisualization(viz.data);
      setRecommendations(recs.data);
      setAccessibilityData(accessibility.data);
    } catch (err) {
      console.error('Error loading audit data:', err);
    }
    setLoading(false);
  };

  // Export functions
  const exportAuditPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/audit/export-pdf?lang=${lang}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-declic-rapport-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
    setExporting(false);
  };

  const exportAccessibilityPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/audit/accessibility/export-pdf?lang=${lang}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `accessibilite-metiers-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
    setExporting(false);
  };

  const exportMethodologyPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/audit/export-methodology-pdf?lang=${lang}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `methodologie-declic-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export methodology error:', err);
    }
    setExporting(false);
  };

  const exportUserGuidePDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/audit/export-user-guide-pdf?lang=${lang}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', lang === 'fr' ? `guide-utilisateur-declic-${new Date().toISOString().split('T')[0]}.pdf` : `gebruiksaanwijzing-declic-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export user guide error:', err);
    }
    setExporting(false);
  };

  // Snapshot functions
  const loadSnapshots = async () => {
    try {
      const response = await axios.get(`${API}/audit/snapshots?lang=${lang}`);
      setSnapshots(response.data.snapshots || []);
    } catch (err) {
      console.error('Error loading snapshots:', err);
    }
  };

  const createSnapshot = async () => {
    setCreatingSnapshot(true);
    try {
      const params = new URLSearchParams({ lang });
      if (snapshotName) params.append('name', snapshotName);
      if (snapshotDescription) params.append('description', snapshotDescription);
      
      await axios.post(`${API}/audit/snapshots?${params.toString()}`);
      setSnapshotName('');
      setSnapshotDescription('');
      await loadSnapshots();
    } catch (err) {
      console.error('Error creating snapshot:', err);
    }
    setCreatingSnapshot(false);
  };

  const compareWithCurrent = async (snapshotId) => {
    setComparingSnapshot(true);
    setSelectedSnapshot(snapshotId);
    try {
      const response = await axios.get(`${API}/audit/snapshots/${snapshotId}/compare-current?lang=${lang}`);
      setComparison(response.data);
    } catch (err) {
      console.error('Error comparing snapshot:', err);
    }
    setComparingSnapshot(false);
  };

  const deleteSnapshot = async (snapshotId) => {
    if (!window.confirm(lang === 'fr' ? 'Supprimer ce snapshot ?' : 'Dit snapshot verwijderen?')) return;
    try {
      await axios.delete(`${API}/audit/snapshots/${snapshotId}`);
      await loadSnapshots();
      if (selectedSnapshot === snapshotId) {
        setSelectedSnapshot(null);
        setComparison(null);
      }
    } catch (err) {
      console.error('Error deleting snapshot:', err);
    }
  };

  // Load snapshots when tab changes to comparison
  useEffect(() => {
    if (activeTab === 'comparison' && isAuthenticated) {
      loadSnapshots();
      // Load algorithm comparison data
      axios.get(`${API}/audit/algorithm-comparison?lang=${lang}`)
        .then(res => setAlgorithmComparison(res.data))
        .catch(err => console.error('Error loading algorithm comparison:', err));
    }
  }, [activeTab, isAuthenticated, lang]);

  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1D1D1D] to-[#A4187A] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#A4187A] to-[#FB6823] flex items-center justify-center mx-auto mb-4">
              <Database className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#1D1D1D]">
              {lang === 'fr' ? 'Audit & Transparence' : 'Audit & Transparantie'}
            </h1>
            <p className="text-[#1D1D1D]/60 text-sm mt-2">
              {lang === 'fr' ? 'Module d\'analyse du système de recommandation' : 'Analysmodule van het aanbevelingssysteem'}
            </p>
          </div>
          
          <div className="flex justify-center gap-2 mb-4">
            <button onClick={() => setLang('fr')} className={`px-4 py-2 rounded-lg text-sm font-medium ${lang === 'fr' ? 'bg-[#A4187A] text-white' : 'bg-gray-100'}`}>FR</button>
            <button onClick={() => setLang('nl')} className={`px-4 py-2 rounded-lg text-sm font-medium ${lang === 'nl' ? 'bg-[#FB6823] text-white' : 'bg-gray-100'}`}>NL</button>
          </div>
          
          <form onSubmit={handleLogin}>
            <div className="relative mb-4">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={lang === 'fr' ? 'Mot de passe' : 'Wachtwoord'}
                className="w-full px-4 py-3 pr-12 rounded-xl border-2 border-[#E0E0E0] focus:border-[#A4187A] focus:outline-none"
                data-testid="audit-password-input"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1D1D1D]/40">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {authError && <p className="text-red-500 text-sm mb-4">{authError}</p>}
            <button type="submit" className="w-full py-3 bg-gradient-to-r from-[#A4187A] to-[#FB6823] text-white rounded-xl font-bold" data-testid="audit-login-btn">
              {lang === 'fr' ? 'Accéder à l\'Audit' : 'Naar Audit'}
            </button>
          </form>
          
          <button onClick={() => navigate('/admin')} className="w-full mt-4 py-2 text-[#A4187A] text-sm hover:underline flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            {lang === 'fr' ? 'Retour au Dashboard Admin' : 'Terug naar Admin Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#A4187A] animate-spin mx-auto mb-4" />
          <p className="text-[#1D1D1D]/60">{lang === 'fr' ? 'Chargement de l\'audit...' : 'Audit laden...'}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'methodology', label: lang === 'fr' ? 'Méthodologie' : 'Methodologie', icon: BookOpen },
    { id: 'careers', label: lang === 'fr' ? 'Mapping Métiers' : 'Beroepen Mapping', icon: Briefcase },
    { id: 'accessibility', label: lang === 'fr' ? 'Accessibilité' : 'Toegankelijkheid', icon: Target },
    { id: 'comparison', label: lang === 'fr' ? 'Avant/Après' : 'Voor/Na', icon: GitCompare },
    { id: 'questions', label: lang === 'fr' ? 'Impact Questions' : 'Vragen Impact', icon: BarChart3 },
    { id: 'bias', label: lang === 'fr' ? 'Détection Biais' : 'Vooroordelen', icon: AlertTriangle },
    { id: 'links', label: lang === 'fr' ? 'Liens Manquants' : 'Ontbrekende Links', icon: Link2 },
    { id: 'education', label: lang === 'fr' ? 'Formations' : 'Opleidingen', icon: GraduationCap },
    { id: 'visualization', label: lang === 'fr' ? 'Visualisation' : 'Visualisatie', icon: Network },
    { id: 'recommendations', label: lang === 'fr' ? 'Recommandations' : 'Aanbevelingen', icon: Lightbulb }
  ];

  return (
    <div className="min-h-screen bg-[#EBEBEB]">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1D1D1D] via-[#A4187A] to-[#9C27B0] text-white py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  {lang === 'fr' ? 'Audit & Transparence' : 'Audit & Transparantie'}
                </h1>
                <p className="text-white/70 text-sm">{lang === 'fr' ? 'Analyse du système de recommandation' : 'Analyse van het aanbevelingssysteem'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Export Buttons */}
              <button
                onClick={exportAuditPDF}
                disabled={exporting}
                className="px-3 py-2 bg-white/20 rounded-lg hover:bg-white/30 flex items-center gap-2 text-sm"
                data-testid="export-audit-pdf"
              >
                <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
                {lang === 'fr' ? 'Export Audit' : 'Export Audit'}
              </button>
              <div className="flex bg-white/20 rounded-lg p-1">
                <button onClick={() => { setLang('fr'); loadData(); }} className={`px-3 py-1 rounded text-sm ${lang === 'fr' ? 'bg-white text-[#A4187A]' : ''}`}>FR</button>
                <button onClick={() => { setLang('nl'); loadData(); }} className={`px-3 py-1 rounded text-sm ${lang === 'nl' ? 'bg-white text-[#FB6823]' : ''}`}>NL</button>
              </div>
              <button onClick={loadData} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap text-sm transition-colors ${
                  activeTab === tab.id ? 'bg-white text-[#A4187A] font-medium' : 'bg-white/20 hover:bg-white/30'
                }`}
                data-testid={`audit-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto p-4 space-y-4">
        
        {/* METHODOLOGY & TRANSPARENCY TAB */}
        {activeTab === 'methodology' && (
          <div className="space-y-6">
            {/* Header with Export Buttons */}
            <div className="bg-gradient-to-r from-[#A4187A] to-[#FB6823] rounded-2xl p-6 text-white">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    {lang === 'fr' ? 'Méthodologie & Transparence' : 'Methodologie & Transparantie'}
                  </h2>
                  <p className="text-white/90">
                    {lang === 'fr' 
                      ? 'Comprendre comment Déclic aide les jeunes à explorer leur avenir professionnel'
                      : 'Begrijp hoe Déclic jongeren helpt hun professionele toekomst te verkennen'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={exportMethodologyPDF}
                    disabled={exporting}
                    className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg flex items-center gap-2 text-sm whitespace-nowrap"
                    data-testid="export-methodology-pdf"
                  >
                    <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
                    {lang === 'fr' ? 'PDF Méthodologie' : 'PDF Methodologie'}
                  </button>
                  <button
                    onClick={exportUserGuidePDF}
                    disabled={exporting}
                    className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg flex items-center gap-2 text-sm whitespace-nowrap"
                    data-testid="export-user-guide-pdf"
                  >
                    <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
                    {lang === 'fr' ? 'PDF Guide Utilisateur' : 'PDF Gebruiksaanwijzing'}
                  </button>
                </div>
              </div>
            </div>

            {/* SECTION 1: Mission & Raison d'être */}
            <div className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h3 className="text-xl font-bold text-[#A4187A] mb-4 flex items-center gap-2">
                <Target className="w-6 h-6" />
                {lang === 'fr' ? 'Notre Mission' : 'Onze Missie'}
              </h3>
              <div className="space-y-4 text-[#1D1D1D]/80">
                <p className="text-lg font-medium text-[#1D1D1D]">
                  {lang === 'fr' 
                    ? 'Aider les jeunes de 15 à 25 ans à découvrir des pistes professionnelles alignées avec leur personnalité.'
                    : 'Jongeren van 15 tot 25 jaar helpen om professionele paden te ontdekken die aansluiten bij hun persoonlijkheid.'}
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-[#EBEBEB] rounded-lg p-4">
                    <h4 className="font-bold text-[#A4187A] mb-2">{lang === 'fr' ? 'Pourquoi Déclic ?' : 'Waarom Déclic?'}</h4>
                    <ul className="space-y-2 text-sm">
                      <li>• {lang === 'fr' ? 'Beaucoup de jeunes ne connaissent pas tous les métiers qui existent' : 'Veel jongeren kennen niet alle bestaande beroepen'}</li>
                      <li>• {lang === 'fr' ? 'Le choix d\'orientation est souvent stressant et mal accompagné' : 'De studiekeuze is vaak stressvol en slecht begeleid'}</li>
                      <li>• {lang === 'fr' ? 'Les tests traditionnels sont souvent trop académiques' : 'Traditionele tests zijn vaak te academisch'}</li>
                    </ul>
                  </div>
                  <div className="bg-[#EBEBEB] rounded-lg p-4">
                    <h4 className="font-bold text-[#FB6823] mb-2">{lang === 'fr' ? 'Notre approche' : 'Onze aanpak'}</h4>
                    <ul className="space-y-2 text-sm">
                      <li>• {lang === 'fr' ? 'Un test ludique et rapide (5-10 minutes)' : 'Een leuke en snelle test (5-10 minuten)'}</li>
                      <li>• {lang === 'fr' ? 'Basé sur la philosophie japonaise Ikigai' : 'Gebaseerd op de Japanse Ikigai-filosofie'}</li>
                      <li>• {lang === 'fr' ? 'Suggestions personnalisées parmi 214 métiers belges' : 'Gepersonaliseerde suggesties uit 214 Belgische beroepen'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 2: Méthodologie Ikigai */}
            <div className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h3 className="text-xl font-bold text-[#A4187A] mb-4 flex items-center gap-2">
                <BookOpen className="w-6 h-6" />
                {lang === 'fr' ? 'La Méthode Ikigai' : 'De Ikigai Methode'}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-[#1D1D1D]/80 mb-4">
                    {lang === 'fr' 
                      ? 'L\'Ikigai est un concept japonais signifiant "raison d\'être". Il se trouve à l\'intersection de 4 dimensions fondamentales :'
                      : 'Ikigai is een Japans concept dat "reden van bestaan" betekent. Het bevindt zich op het snijpunt van 4 fundamentele dimensies:'}
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border-l-4 border-red-400">
                      <span className="text-2xl">❤️</span>
                      <div>
                        <h5 className="font-bold text-red-700">{lang === 'fr' ? 'PASSION' : 'PASSIE'}</h5>
                        <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' ? 'Ce que tu aimes faire' : 'Wat je graag doet'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
                      <span className="text-2xl">⭐</span>
                      <div>
                        <h5 className="font-bold text-yellow-700">{lang === 'fr' ? 'TALENT' : 'TALENT'}</h5>
                        <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' ? 'Ce dans quoi tu excelles' : 'Waar je goed in bent'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border-l-4 border-green-400">
                      <span className="text-2xl">🌍</span>
                      <div>
                        <h5 className="font-bold text-green-700">{lang === 'fr' ? 'MISSION' : 'MISSIE'}</h5>
                        <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' ? 'Ce dont le monde a besoin' : 'Wat de wereld nodig heeft'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                      <span className="text-2xl">💼</span>
                      <div>
                        <h5 className="font-bold text-blue-700">{lang === 'fr' ? 'PROFESSION' : 'PROFESSIE'}</h5>
                        <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' ? 'Ce pour quoi tu peux être payé' : 'Waarvoor je betaald kunt worden'}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="relative w-64 h-64">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 bg-[#A4187A] rounded-full flex items-center justify-center text-white font-bold text-sm">IKIGAI</div>
                    </div>
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-red-200/50 rounded-full"></div>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-green-200/50 rounded-full"></div>
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-32 h-32 bg-yellow-200/50 rounded-full"></div>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-32 h-32 bg-blue-200/50 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 3: Fonctionnement du Questionnaire */}
            <div className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h3 className="text-xl font-bold text-[#A4187A] mb-4 flex items-center gap-2">
                <BarChart3 className="w-6 h-6" />
                {lang === 'fr' ? 'Fonctionnement du Questionnaire' : 'Werking van de Vragenlijst'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-[#EBEBEB] rounded-xl">
                  <p className="text-4xl font-bold text-[#A4187A]">24</p>
                  <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' ? 'Questions' : 'Vragen'}</p>
                </div>
                <div className="text-center p-4 bg-[#EBEBEB] rounded-xl">
                  <p className="text-4xl font-bold text-[#FB6823]">10</p>
                  <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' ? 'Domaines professionnels' : 'Professionele domeinen'}</p>
                </div>
                <div className="text-center p-4 bg-[#EBEBEB] rounded-xl">
                  <p className="text-4xl font-bold text-[#A4187A]">214</p>
                  <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' ? 'Métiers analysés' : 'Geanalyseerde beroepen'}</p>
                </div>
              </div>
              <div className="space-y-4">
                <h4 className="font-bold">{lang === 'fr' ? 'Les 10 domaines professionnels :' : 'De 10 professionele domeinen:'}</h4>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'health', fr: 'Santé', nl: 'Gezondheid', color: 'bg-red-100 text-red-700' },
                    { key: 'social', fr: 'Social', nl: 'Sociaal', color: 'bg-pink-100 text-pink-700' },
                    { key: 'education', fr: 'Éducation', nl: 'Onderwijs', color: 'bg-purple-100 text-purple-700' },
                    { key: 'creativity', fr: 'Créativité', nl: 'Creativiteit', color: 'bg-indigo-100 text-indigo-700' },
                    { key: 'technology', fr: 'Technologie', nl: 'Technologie', color: 'bg-blue-100 text-blue-700' },
                    { key: 'technical', fr: 'Technique', nl: 'Technisch', color: 'bg-cyan-100 text-cyan-700' },
                    { key: 'science', fr: 'Science', nl: 'Wetenschap', color: 'bg-teal-100 text-teal-700' },
                    { key: 'business', fr: 'Business', nl: 'Business', color: 'bg-green-100 text-green-700' },
                    { key: 'administration', fr: 'Gestion & Organisation', nl: 'Beheer & Organisatie', color: 'bg-yellow-100 text-yellow-700' },
                    { key: 'sports', fr: 'Sport & Bien-être', nl: 'Sport & Welzijn', color: 'bg-orange-100 text-orange-700' }
                  ].map(domain => (
                    <span key={domain.key} className={`px-3 py-1 rounded-full text-sm font-medium ${domain.color}`}>
                      {lang === 'fr' ? domain.fr : domain.nl}
                    </span>
                  ))}
                </div>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 className="font-bold text-blue-700 mb-2">{lang === 'fr' ? 'Comment ça marche ?' : 'Hoe werkt het?'}</h5>
                  <ol className="text-sm space-y-2 text-[#1D1D1D]/80">
                    <li>1. {lang === 'fr' ? 'Chaque réponse attribue des points à un ou plusieurs domaines' : 'Elk antwoord kent punten toe aan een of meer domeinen'}</li>
                    <li>2. {lang === 'fr' ? 'Le système calcule ton profil sur les 10 domaines (graphique radar)' : 'Het systeem berekent je profiel over de 10 domeinen (radargrafiek)'}</li>
                    <li>3. {lang === 'fr' ? 'Les métiers sont triés selon leur correspondance avec ton profil' : 'Beroepen worden gesorteerd op basis van hun overeenkomst met je profiel'}</li>
                    <li>4. {lang === 'fr' ? 'Tu reçois 12 suggestions personnalisées parmi 214 métiers' : 'Je ontvangt 12 gepersonaliseerde suggesties uit 214 beroepen'}</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* SECTION 4: Précautions contre les biais */}
            <div className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h3 className="text-xl font-bold text-[#A4187A] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                {lang === 'fr' ? 'Précautions contre les Biais' : 'Voorzorgsmaatregelen tegen Vooroordelen'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                    <h5 className="font-bold text-green-700 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      {lang === 'fr' ? 'Ce que nous faisons' : 'Wat we doen'}
                    </h5>
                    <ul className="mt-2 text-sm space-y-1 text-[#1D1D1D]/80">
                      <li>• {lang === 'fr' ? 'Randomisation des suggestions (±15%) pour éviter la répétition' : 'Randomisatie van suggesties (±15%) om herhaling te voorkomen'}</li>
                      <li>• {lang === 'fr' ? 'Équilibrage entre domaines (max 3 métiers/domaine)' : 'Balancering tussen domeinen (max 3 beroepen/domein)'}</li>
                      <li>• {lang === 'fr' ? 'Inclusion de métiers "découverte" pour élargir les horizons' : 'Inclusie van "ontdekkings" beroepen om horizonten te verbreden'}</li>
                      <li>• {lang === 'fr' ? 'Aucune question liée au genre, origine ou situation sociale' : 'Geen vragen over geslacht, afkomst of sociale situatie'}</li>
                      <li>• {lang === 'fr' ? 'Monitoring continu du taux de couverture des métiers' : 'Continue monitoring van de dekkingsgraad van beroepen'}</li>
                    </ul>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                    <h5 className="font-bold text-yellow-700 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      {lang === 'fr' ? 'Limites à connaître' : 'Beperkingen om te kennen'}
                    </h5>
                    <ul className="mt-2 text-sm space-y-1 text-[#1D1D1D]/80">
                      <li>• {lang === 'fr' ? 'Ce test n\'est pas un diagnostic, mais une exploration' : 'Deze test is geen diagnose, maar een verkenning'}</li>
                      <li>• {lang === 'fr' ? 'Les résultats dépendent de la sincérité des réponses' : 'De resultaten hangen af van de oprechtheid van de antwoorden'}</li>
                      <li>• {lang === 'fr' ? 'Un métier peut te convenir même s\'il n\'est pas suggéré' : 'Een beroep kan bij je passen, ook als het niet gesuggereerd wordt'}</li>
                      <li>• {lang === 'fr' ? 'Les intérêts évoluent avec le temps' : 'Interesses evolueren met de tijd'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 5: Taux de couverture */}
            <div className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h3 className="text-xl font-bold text-[#A4187A] mb-4 flex items-center gap-2">
                <Target className="w-6 h-6" />
                {lang === 'fr' ? 'Taux de Couverture des Métiers' : 'Dekkingsgraad van Beroepen'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200">
                  <p className="text-4xl font-bold text-green-600">94.9%</p>
                  <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' ? 'Couverture théorique' : 'Theoretische dekking'}</p>
                  <p className="text-xs text-green-600">{lang === 'fr' ? '203 métiers sur 214 peuvent être suggérés' : '203 van 214 beroepen kunnen worden voorgesteld'}</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-4xl font-bold text-blue-600">12</p>
                  <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' ? 'Suggestions par session' : 'Suggesties per sessie'}</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-200">
                  <p className="text-4xl font-bold text-[#A4187A]">3</p>
                  <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' ? 'Métiers "découverte"' : '"Ontdekkings" beroepen'}</p>
                  <p className="text-xs text-[#A4187A]">{lang === 'fr' ? 'Pour élargir les horizons' : 'Om horizonten te verbreden'}</p>
                </div>
              </div>
              <div className="p-4 bg-[#EBEBEB] rounded-lg">
                <p className="text-sm text-[#1D1D1D]/80">
                  <strong>{lang === 'fr' ? 'Note :' : 'Opmerking:'}</strong> {lang === 'fr' 
                    ? 'Le taux de couverture mesure combien de métiers différents peuvent être suggérés selon les profils des utilisateurs. Un taux élevé garantit que chaque jeune peut découvrir des métiers adaptés à son profil unique.'
                    : 'De dekkingsgraad meet hoeveel verschillende beroepen kunnen worden voorgesteld op basis van gebruikersprofielen. Een hoog percentage garandeert dat elke jongere beroepen kan ontdekken die passen bij zijn unieke profiel.'}
                </p>
              </div>
            </div>

            {/* SECTION 6: Mode d'emploi */}
            <div className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h3 className="text-xl font-bold text-[#A4187A] mb-4 flex items-center gap-2">
                <Users className="w-6 h-6" />
                {lang === 'fr' ? 'Mode d\'Emploi pour l\'Utilisateur' : 'Gebruiksaanwijzing'}
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-[#EBEBEB] rounded-lg">
                  <div className="w-10 h-10 bg-[#A4187A] rounded-full flex items-center justify-center text-white font-bold shrink-0">1</div>
                  <div>
                    <h5 className="font-bold">{lang === 'fr' ? 'Commence le test' : 'Start de test'}</h5>
                    <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' 
                      ? 'Clique sur "Commencer le test" et réponds aux questions préliminaires (âge, situation).'
                      : 'Klik op "Start de test" en beantwoord de voorbereidende vragen (leeftijd, situatie).'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#EBEBEB] rounded-lg">
                  <div className="w-10 h-10 bg-[#FB6823] rounded-full flex items-center justify-center text-white font-bold shrink-0">2</div>
                  <div>
                    <h5 className="font-bold">{lang === 'fr' ? 'Réponds aux 24 questions' : 'Beantwoord de 24 vragen'}</h5>
                    <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' 
                      ? 'Choisis la réponse qui te correspond le mieux. Il n\'y a pas de bonne ou mauvaise réponse !'
                      : 'Kies het antwoord dat het beste bij je past. Er is geen goed of fout antwoord!'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#EBEBEB] rounded-lg">
                  <div className="w-10 h-10 bg-[#A4187A] rounded-full flex items-center justify-center text-white font-bold shrink-0">3</div>
                  <div>
                    <h5 className="font-bold">{lang === 'fr' ? 'Découvre ton profil' : 'Ontdek je profiel'}</h5>
                    <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' 
                      ? 'Visualise ton graphique radar et découvre tes domaines dominants.'
                      : 'Bekijk je radargrafiek en ontdek je dominante domeinen.'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#EBEBEB] rounded-lg">
                  <div className="w-10 h-10 bg-[#FB6823] rounded-full flex items-center justify-center text-white font-bold shrink-0">4</div>
                  <div>
                    <h5 className="font-bold">{lang === 'fr' ? 'Explore les métiers suggérés' : 'Verken de voorgestelde beroepen'}</h5>
                    <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' 
                      ? 'Parcours les 12 métiers suggérés. Clique sur ceux qui t\'intéressent pour en savoir plus.'
                      : 'Bekijk de 12 voorgestelde beroepen. Klik op degene die je interesseren voor meer informatie.'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#EBEBEB] rounded-lg">
                  <div className="w-10 h-10 bg-[#A4187A] rounded-full flex items-center justify-center text-white font-bold shrink-0">5</div>
                  <div>
                    <h5 className="font-bold">{lang === 'fr' ? 'Approfondis (Étape 2)' : 'Verdiep je (Stap 2)'}</h5>
                    <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' 
                      ? 'Sélectionne 2-3 métiers et réponds à 7 questions supplémentaires pour affiner ton classement.'
                      : 'Selecteer 2-3 beroepen en beantwoord 7 extra vragen om je rangschikking te verfijnen.'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#EBEBEB] rounded-lg">
                  <div className="w-10 h-10 bg-[#FB6823] rounded-full flex items-center justify-center text-white font-bold shrink-0">6</div>
                  <div>
                    <h5 className="font-bold">{lang === 'fr' ? 'Télécharge & Partage' : 'Download & Deel'}</h5>
                    <p className="text-sm text-[#1D1D1D]/70">{lang === 'fr' 
                      ? 'Télécharge tes résultats en PDF ou en image. Partage-les avec tes proches ou ton conseiller d\'orientation.'
                      : 'Download je resultaten als PDF of afbeelding. Deel ze met je naasten of studiekeuzeadviseur.'}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-gradient-to-r from-[#A4187A]/10 to-[#FB6823]/10 rounded-lg border border-[#A4187A]/20">
                <h5 className="font-bold text-[#A4187A] mb-2">{lang === 'fr' ? 'Conseils pour de meilleurs résultats' : 'Tips voor betere resultaten'}</h5>
                <ul className="text-sm space-y-1 text-[#1D1D1D]/80">
                  <li>✓ {lang === 'fr' ? 'Réponds spontanément, sans trop réfléchir' : 'Antwoord spontaan, zonder te veel na te denken'}</li>
                  <li>✓ {lang === 'fr' ? 'Sois honnête avec toi-même' : 'Wees eerlijk tegen jezelf'}</li>
                  <li>✓ {lang === 'fr' ? 'Ne te limite pas aux métiers que tu connais déjà' : 'Beperk je niet tot beroepen die je al kent'}</li>
                  <li>✓ {lang === 'fr' ? 'Explore les métiers "découverte" avec curiosité' : 'Verken de "ontdekkings" beroepen met nieuwsgierigheid'}</li>
                  <li>✓ {lang === 'fr' ? 'Refais le test dans quelques mois pour voir ton évolution' : 'Doe de test opnieuw over een paar maanden om je evolutie te zien'}</li>
                </ul>
              </div>
            </div>

            {/* SECTION 7: Données et Confidentialité */}
            <div className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
              <h3 className="text-xl font-bold text-[#A4187A] mb-4 flex items-center gap-2">
                <Lock className="w-6 h-6" />
                {lang === 'fr' ? 'Données et Confidentialité' : 'Gegevens en Privacy'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <h5 className="font-bold text-green-700 mb-2">{lang === 'fr' ? 'Ce que nous collectons' : 'Wat we verzamelen'}</h5>
                  <ul className="text-sm space-y-1 text-[#1D1D1D]/80">
                    <li>• {lang === 'fr' ? 'Réponses au questionnaire (anonymisées)' : 'Antwoorden op de vragenlijst (geanonimiseerd)'}</li>
                    <li>• {lang === 'fr' ? 'Localisation approximative (région)' : 'Geschatte locatie (regio)'}</li>
                    <li>• {lang === 'fr' ? 'Tranche d\'âge et situation scolaire' : 'Leeftijdscategorie en schoolsituatie'}</li>
                  </ul>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h5 className="font-bold text-blue-700 mb-2">{lang === 'fr' ? 'Ce que nous ne faisons PAS' : 'Wat we NIET doen'}</h5>
                  <ul className="text-sm space-y-1 text-[#1D1D1D]/80">
                    <li>• {lang === 'fr' ? 'Vendre vos données' : 'Uw gegevens verkopen'}</li>
                    <li>• {lang === 'fr' ? 'Créer de profil nominatif' : 'Nominatieve profielen maken'}</li>
                    <li>• {lang === 'fr' ? 'Partager avec des tiers commerciaux' : 'Delen met commerciële derden'}</li>
                  </ul>
                </div>
              </div>
              <p className="mt-4 text-sm text-[#1D1D1D]/60">
                {lang === 'fr' 
                  ? 'Les données collectées servent uniquement à améliorer l\'algorithme et produire des statistiques agrégées pour les décideurs de l\'éducation.'
                  : 'De verzamelde gegevens worden uitsluitend gebruikt om het algoritme te verbeteren en geaggregeerde statistieken te produceren voor onderwijsbeslissers.'}
              </p>
            </div>
          </div>
        )}

        {/* PART 1: Career Mapping */}
        {activeTab === 'careers' && careerMapping && (
          <>
            <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1D1D1D]/40" />
                  <input
                    type="text"
                    placeholder={lang === 'fr' ? 'Rechercher un métier...' : 'Zoek een beroep...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-[#E0E0E0] focus:border-[#A4187A] focus:outline-none"
                  />
                </div>
                <select
                  value={domainFilter}
                  onChange={(e) => setDomainFilter(e.target.value)}
                  className="px-4 py-2 rounded-lg border border-[#E0E0E0] focus:border-[#A4187A] focus:outline-none"
                >
                  <option value="">{lang === 'fr' ? 'Tous les domaines' : 'Alle domeinen'}</option>
                  {['health', 'social', 'education', 'creativity', 'technology', 'technical', 'science', 'business', 'administration', 'sports'].map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <span className="text-sm text-[#1D1D1D]/60">{careerMapping.total} métiers</span>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-[#A4187A]">
                      <th 
                        className="text-left py-2 px-2 cursor-pointer hover:bg-purple-50 select-none"
                        onClick={() => handleSort('name')}
                      >
                        <span className="flex items-center">
                          {lang === 'fr' ? 'Métier' : 'Beroep'}
                          <SortIcon columnKey="name" />
                        </span>
                      </th>
                      <th 
                        className="text-left py-2 px-2 cursor-pointer hover:bg-purple-50 select-none"
                        onClick={() => handleSort('domains')}
                      >
                        <span className="flex items-center">
                          {lang === 'fr' ? 'Domaines' : 'Domeinen'}
                          <SortIcon columnKey="domains" />
                        </span>
                      </th>
                      <th 
                        className="text-center py-2 px-2 cursor-pointer hover:bg-purple-50 select-none"
                        onClick={() => handleSort('suggested_count')}
                      >
                        <span className="flex items-center justify-center">
                          {lang === 'fr' ? 'Suggéré' : 'Voorgesteld'}
                          <SortIcon columnKey="suggested_count" />
                        </span>
                      </th>
                      <th 
                        className="text-center py-2 px-2 cursor-pointer hover:bg-purple-50 select-none"
                        onClick={() => handleSort('selected_count')}
                      >
                        <span className="flex items-center justify-center">
                          {lang === 'fr' ? 'Sélectionné' : 'Geselecteerd'}
                          <SortIcon columnKey="selected_count" />
                        </span>
                      </th>
                      <th 
                        className="text-center py-2 px-2 cursor-pointer hover:bg-purple-50 select-none"
                        onClick={() => handleSort('selection_rate')}
                      >
                        <span className="flex items-center justify-center">
                          Taux
                          <SortIcon columnKey="selection_rate" />
                        </span>
                      </th>
                      <th 
                        className="text-center py-2 px-2 cursor-pointer hover:bg-purple-50 select-none"
                        onClick={() => handleSort('associated_questions_count')}
                      >
                        <span className="flex items-center justify-center">
                          {lang === 'fr' ? 'Questions' : 'Vragen'}
                          <SortIcon columnKey="associated_questions_count" />
                        </span>
                      </th>
                      <th 
                        className="text-center py-2 px-2 cursor-pointer hover:bg-purple-50 select-none"
                        onClick={() => handleSort('formations_count')}
                      >
                        <span className="flex items-center justify-center">
                          {lang === 'fr' ? 'Formations' : 'Opleidingen'}
                          <SortIcon columnKey="formations_count" />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedCareers(careerMapping.careers || [])
                      .filter(c => 
                        (!searchTerm || c.name.toLowerCase().includes(searchTerm.toLowerCase())) &&
                        (!domainFilter || c.domains.includes(domainFilter))
                      )
                      .slice(0, 50)
                      .map((career, idx) => (
                        <React.Fragment key={idx}>
                          <tr className="border-b border-[#E0E0E0]/50 hover:bg-purple-50 cursor-pointer" onClick={() => toggleExpanded(career.id)}>
                            <td className="py-2 px-2">
                              <div className="flex items-center gap-2">
                                {expandedItems[career.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                <span className="font-medium">{career.name}</span>
                              </div>
                            </td>
                            <td className="py-2 px-2">
                              <div className="flex flex-wrap gap-1">
                                {career.domain_labels?.slice(0, 2).map((d, i) => (
                                  <span key={i} className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">{d}</span>
                                ))}
                              </div>
                            </td>
                            <td className="py-2 px-2 text-center">{career.suggested_count}</td>
                            <td className="py-2 px-2 text-center">{career.selected_count}</td>
                            <td className="py-2 px-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                career.selection_rate > 20 ? 'bg-green-100 text-green-700' :
                                career.selection_rate > 5 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {career.selection_rate}%
                              </span>
                            </td>
                            <td className="py-2 px-2 text-center">{career.associated_questions_count}</td>
                            <td className="py-2 px-2 text-center">{career.formations_count}</td>
                          </tr>
                          {expandedItems[career.id] && (
                            <tr className="bg-purple-50">
                              <td colSpan={7} className="p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <h4 className="font-medium text-sm mb-2">{lang === 'fr' ? 'Questions associées' : 'Gekoppelde vragen'}</h4>
                                    <ul className="space-y-1 text-xs">
                                      {career.associated_questions?.map((q, i) => (
                                        <li key={i} className="flex gap-2">
                                          <span className="text-purple-600">Q{q.question_id}</span>
                                          <span className="truncate">{q.question}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-sm mb-2">{lang === 'fr' ? 'Compétences' : 'Vaardigheden'}</h4>
                                    <div className="flex flex-wrap gap-1">
                                      {career.skills?.map((s, i) => (
                                        <span key={i} className="text-xs px-2 py-0.5 bg-gray-100 rounded">{s}</span>
                                      ))}
                                    </div>
                                    <p className="text-xs text-[#1D1D1D]/60 mt-2">{career.studies}</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* PART 2: Accessibility Score Tab */}
        {activeTab === 'accessibility' && accessibilityData && (
          <>
            {/* Header with Export Button */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-[#1D1D1D]">
                  {lang === 'fr' ? 'Score d\'Accessibilité des Métiers' : 'Toegankelijkheidsscore Beroepen'}
                </h2>
                <p className="text-sm text-[#1D1D1D]/60">
                  {lang === 'fr' 
                    ? 'Analyse de la facilité d\'atteindre chaque métier via le questionnaire' 
                    : 'Analyse van hoe gemakkelijk elk beroep bereikbaar is via de vragenlijst'}
                </p>
              </div>
              <button
                onClick={exportAccessibilityPDF}
                disabled={exporting}
                className="px-4 py-2 bg-[#A4187A] text-white rounded-lg hover:bg-[#5a0b91] flex items-center gap-2"
                data-testid="export-accessibility-pdf"
              >
                <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
                {lang === 'fr' ? 'Export PDF' : 'Export PDF'}
              </button>
            </div>

            {/* Summary Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] text-center">
                <p className="text-3xl font-bold text-[#A4187A]">{accessibilityData.summary?.average_score || 0}%</p>
                <p className="text-xs text-[#1D1D1D]/60">{lang === 'fr' ? 'Score Moyen' : 'Gemiddelde Score'}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border-2 border-green-500 text-center">
                <p className="text-3xl font-bold text-green-600">{accessibilityData.summary?.highly_accessible || 0}</p>
                <p className="text-xs text-[#1D1D1D]/60">{lang === 'fr' ? 'Très Accessible' : 'Zeer Toegankelijk'}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border-2 border-yellow-500 text-center">
                <p className="text-3xl font-bold text-yellow-600">{accessibilityData.summary?.moderately_accessible || 0}</p>
                <p className="text-xs text-[#1D1D1D]/60">{lang === 'fr' ? 'Modéré' : 'Matig'}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border-2 border-orange-500 text-center">
                <p className="text-3xl font-bold text-orange-600">{accessibilityData.summary?.low_accessibility || 0}</p>
                <p className="text-xs text-[#1D1D1D]/60">{lang === 'fr' ? 'Faible' : 'Laag'}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border-2 border-red-500 text-center">
                <p className="text-3xl font-bold text-red-600">{accessibilityData.summary?.critical || 0}</p>
                <p className="text-xs text-[#1D1D1D]/60">{lang === 'fr' ? 'Critique' : 'Kritiek'}</p>
              </div>
            </div>

            {/* Distribution Chart */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                <h3 className="text-lg font-bold mb-4">{lang === 'fr' ? 'Distribution des Scores' : 'Scoreverdeling'}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: lang === 'fr' ? 'Très Accessible (75-100)' : 'Zeer Toegankelijk', value: accessibilityData.summary?.highly_accessible || 0, fill: '#4CAF50' },
                        { name: lang === 'fr' ? 'Modéré (50-74)' : 'Matig', value: accessibilityData.summary?.moderately_accessible || 0, fill: '#FFC107' },
                        { name: lang === 'fr' ? 'Faible (25-49)' : 'Laag', value: accessibilityData.summary?.low_accessibility || 0, fill: '#FF9800' },
                        { name: lang === 'fr' ? 'Critique (<25)' : 'Kritiek', value: accessibilityData.summary?.critical || 0, fill: '#F44336' }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${value}`}
                    >
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                <h3 className="text-lg font-bold mb-4">{lang === 'fr' ? 'Score par Domaine' : 'Score per Domein'}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={accessibilityData.domain_scores || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="domain" angle={-45} textAnchor="end" height={80} fontSize={10} />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Bar dataKey="average_score" fill="#A4187A" name={lang === 'fr' ? 'Score moyen' : 'Gem. score'} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Alerts Section */}
            {accessibilityData.alerts && accessibilityData.alerts.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <h3 className="text-lg font-bold text-red-700 flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5" />
                  {lang === 'fr' ? 'Alertes' : 'Waarschuwingen'}
                </h3>
                <div className="space-y-2">
                  {accessibilityData.alerts.map((alert, idx) => (
                    <div key={idx} className={`p-3 rounded-lg ${
                      alert.type === 'critical' ? 'bg-red-100 border border-red-300' :
                      alert.type === 'warning' ? 'bg-yellow-100 border border-yellow-300' :
                      'bg-blue-100 border border-blue-300'
                    }`}>
                      <div className="flex items-start gap-2">
                        {alert.type === 'critical' ? <XCircle className="w-5 h-5 text-red-600 shrink-0" /> :
                         alert.type === 'warning' ? <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" /> :
                         <Info className="w-5 h-5 text-blue-600 shrink-0" />}
                        <div>
                          <p className="font-medium">{alert.title}</p>
                          <p className="text-sm text-gray-600">{alert.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Career List by Category */}
            <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <h3 className="text-lg font-bold">{lang === 'fr' ? 'Détail par Métier' : 'Detail per Beroep'}</h3>
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1D1D1D]/40" />
                  <input
                    type="text"
                    placeholder={lang === 'fr' ? 'Rechercher...' : 'Zoeken...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-[#E0E0E0] focus:border-[#A4187A] focus:outline-none text-sm"
                  />
                </div>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-[#E0E0E0] focus:border-[#A4187A] focus:outline-none text-sm"
                >
                  <option value="">{lang === 'fr' ? 'Toutes les catégories' : 'Alle categorieën'}</option>
                  <option value="highly_accessible">{lang === 'fr' ? 'Très Accessible' : 'Zeer Toegankelijk'}</option>
                  <option value="moderately_accessible">{lang === 'fr' ? 'Modéré' : 'Matig'}</option>
                  <option value="low_accessibility">{lang === 'fr' ? 'Faible' : 'Laag'}</option>
                  <option value="critical">{lang === 'fr' ? 'Critique' : 'Kritiek'}</option>
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-[#A4187A]">
                      <th className="text-left py-2 px-2">{lang === 'fr' ? 'Métier' : 'Beroep'}</th>
                      <th className="text-center py-2 px-2">Score</th>
                      <th className="text-center py-2 px-2">{lang === 'fr' ? 'Catégorie' : 'Categorie'}</th>
                      <th className="text-center py-2 px-2">{lang === 'fr' ? 'Questions Liées' : 'Gekoppelde Vragen'}</th>
                      <th className="text-left py-2 px-2">{lang === 'fr' ? 'Domaines' : 'Domeinen'}</th>
                      <th className="text-center py-2 px-2">{lang === 'fr' ? 'Fréq. Suggestion' : 'Suggestiefreq.'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(accessibilityData.scores || [])
                      .filter(c => {
                        const matchesSearch = !searchTerm || c.career?.toLowerCase().includes(searchTerm.toLowerCase());
                        const matchesCategory = !categoryFilter || c.category === categoryFilter;
                        return matchesSearch && matchesCategory;
                      })
                      .slice(0, 100)
                      .map((career, idx) => (
                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-2 font-medium">{career.career}</td>
                          <td className="py-2 px-2 text-center">
                            <span className={`font-bold ${
                              career.score >= 75 ? 'text-green-600' :
                              career.score >= 50 ? 'text-yellow-600' :
                              career.score >= 25 ? 'text-orange-600' :
                              'text-red-600'
                            }`}>
                              {career.score}%
                            </span>
                          </td>
                          <td className="py-2 px-2 text-center">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              career.category === 'highly_accessible' ? 'bg-green-100 text-green-700' :
                              career.category === 'moderately_accessible' ? 'bg-yellow-100 text-yellow-700' :
                              career.category === 'low_accessibility' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {career.category === 'highly_accessible' ? (lang === 'fr' ? 'Très Acc.' : 'Zeer Toeg.') :
                               career.category === 'moderately_accessible' ? (lang === 'fr' ? 'Modéré' : 'Matig') :
                               career.category === 'low_accessibility' ? (lang === 'fr' ? 'Faible' : 'Laag') :
                               (lang === 'fr' ? 'Critique' : 'Kritiek')}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-center">{career.linked_questions || 0}</td>
                          <td className="py-2 px-2">
                            <div className="flex flex-wrap gap-1">
                              {(career.domains || []).slice(0, 3).map((d, i) => (
                                <span key={i} className="text-xs px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded">
                                  {d}
                                </span>
                              ))}
                              {(career.domains || []).length > 3 && (
                                <span className="text-xs text-gray-400">+{career.domains.length - 3}</span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-2 text-center">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-[#A4187A] h-2 rounded-full" 
                                style={{ width: `${Math.min(100, (career.suggestion_frequency || 0) / 10)}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500">{career.suggestion_frequency || 0}</span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recommendations */}
            {accessibilityData.recommendations && accessibilityData.recommendations.length > 0 && (
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] mt-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  {lang === 'fr' ? 'Recommandations d\'Amélioration' : 'Verbeteraanbevelingen'}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {accessibilityData.recommendations.map((rec, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                      rec.priority === 'critical' ? 'border-red-500 bg-red-50' :
                      rec.priority === 'high' ? 'border-orange-500 bg-orange-50' :
                      rec.priority === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                      'border-green-500 bg-green-50'
                    }`}>
                      <div className="flex items-start justify-between">
                        <h4 className="font-medium">{rec.title}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          rec.priority === 'critical' ? 'bg-red-200 text-red-800' :
                          rec.priority === 'high' ? 'bg-orange-200 text-orange-800' :
                          rec.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-green-200 text-green-800'
                        }`}>
                          {rec.priority?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">{rec.description}</p>
                      {rec.action && (
                        <p className="text-sm text-[#A4187A] mt-2 font-medium">→ {rec.action}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* COMPARISON TAB - Before/After */}
        {activeTab === 'comparison' && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-[#1D1D1D]">
                  {lang === 'fr' ? 'Comparaison Avant/Après' : 'Voor/Na Vergelijking'}
                </h2>
                <p className="text-sm text-[#1D1D1D]/60">
                  {lang === 'fr' 
                    ? 'Créez des snapshots pour mesurer l\'impact des modifications' 
                    : 'Maak snapshots om de impact van wijzigingen te meten'}
                </p>
              </div>
            </div>

            {/* Algorithm Comparison Section */}
            {algorithmComparison && (
              <div className="bg-gradient-to-r from-[#A4187A] to-[#FB6823] rounded-2xl p-6 text-white mb-6">
                <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6" />
                  {algorithmComparison.summary?.title || 'Impact des Améliorations'}
                </h3>
                <p className="text-white/90 mb-4">{algorithmComparison.summary?.main_achievement}</p>
                
                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold">{algorithmComparison.improvements?.coverage_increase?.before}%</p>
                    <p className="text-sm text-white/70">{lang === 'fr' ? 'Couverture Avant' : 'Dekking Ervoor'}</p>
                    <p className="text-xs text-white/50 mt-1">v{algorithmComparison.old_algorithm?.version}</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold">{algorithmComparison.improvements?.coverage_increase?.after}%</p>
                    <p className="text-sm text-white/70">{lang === 'fr' ? 'Couverture Après' : 'Dekking Erna'}</p>
                    <p className="text-xs text-white/50 mt-1">v{algorithmComparison.new_algorithm?.version}</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold">+{algorithmComparison.improvements?.careers_reachable?.delta}</p>
                    <p className="text-sm text-white/70">{lang === 'fr' ? 'Métiers Débloqués' : 'Beroepen Ontgrendeld'}</p>
                  </div>
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold">+{algorithmComparison.improvements?.suggestions_per_session?.delta}</p>
                    <p className="text-sm text-white/70">{lang === 'fr' ? 'Suggestions/Session' : 'Suggesties/Sessie'}</p>
                  </div>
                </div>

                {/* Before/After Comparison Cards */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Old Algorithm */}
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <h4 className="font-bold">{algorithmComparison.old_algorithm?.name}</h4>
                      <span className="text-xs text-white/60">{algorithmComparison.old_algorithm?.date}</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p>📝 {algorithmComparison.old_algorithm?.questions} questions</p>
                      <p>🎯 {algorithmComparison.old_algorithm?.suggestions_per_session} suggestions/session</p>
                      <p>📊 {algorithmComparison.old_algorithm?.theoretical_coverage}% couverture</p>
                      <p>❌ {lang === 'fr' ? 'Pas de randomisation' : 'Geen randomisatie'}</p>
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/20">
                      <p className="text-xs text-white/70 font-medium mb-1">{lang === 'fr' ? 'Problèmes:' : 'Problemen:'}</p>
                      <ul className="text-xs text-white/60 space-y-1">
                        {algorithmComparison.old_algorithm?.issues?.slice(0, 3).map((issue, i) => (
                          <li key={i}>• {issue}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* New Algorithm */}
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      <h4 className="font-bold">{algorithmComparison.new_algorithm?.name}</h4>
                      <span className="text-xs text-white/60">{algorithmComparison.new_algorithm?.date}</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p>📝 {algorithmComparison.new_algorithm?.questions} questions</p>
                      <p>🎯 {algorithmComparison.new_algorithm?.suggestions_per_session} suggestions/session</p>
                      <p>📊 {algorithmComparison.new_algorithm?.theoretical_coverage}% couverture</p>
                      <p>✅ ±{algorithmComparison.new_algorithm?.randomization_factor}% randomisation</p>
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/20">
                      <p className="text-xs text-white/70 font-medium mb-1">{lang === 'fr' ? 'Améliorations:' : 'Verbeteringen:'}</p>
                      <ul className="text-xs text-white/60 space-y-1">
                        {algorithmComparison.new_algorithm?.improvements?.slice(0, 3).map((imp, i) => (
                          <li key={i}>✓ {imp}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Domain Coverage Comparison */}
                <div className="mt-6">
                  <h4 className="font-bold mb-3">{lang === 'fr' ? 'Couverture par Domaine' : 'Dekking per Domein'}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {algorithmComparison.domain_comparison?.map((domain, i) => (
                      <div key={i} className="bg-white/10 rounded-lg p-2 text-center">
                        <p className="text-xs text-white/70">{domain.label}</p>
                        <div className="flex items-center justify-center gap-1 mt-1">
                          <span className="text-xs text-red-300">{domain.before}%</span>
                          <span className="text-white/50">→</span>
                          <span className="text-sm font-bold text-green-300">{domain.after}%</span>
                        </div>
                        <p className="text-xs text-green-400">+{domain.improvement}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Create Snapshot Section */}
            <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] mb-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Save className="w-5 h-5 text-[#A4187A]" />
                {lang === 'fr' ? 'Créer un Snapshot' : 'Snapshot Aanmaken'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <input
                  type="text"
                  placeholder={lang === 'fr' ? 'Nom du snapshot (optionnel)' : 'Naam van snapshot (optioneel)'}
                  value={snapshotName}
                  onChange={(e) => setSnapshotName(e.target.value)}
                  className="px-4 py-2 rounded-lg border border-[#E0E0E0] focus:border-[#A4187A] focus:outline-none"
                />
                <input
                  type="text"
                  placeholder={lang === 'fr' ? 'Description (optionnel)' : 'Beschrijving (optioneel)'}
                  value={snapshotDescription}
                  onChange={(e) => setSnapshotDescription(e.target.value)}
                  className="px-4 py-2 rounded-lg border border-[#E0E0E0] focus:border-[#A4187A] focus:outline-none"
                />
                <button
                  onClick={createSnapshot}
                  disabled={creatingSnapshot}
                  className="px-4 py-2 bg-[#A4187A] text-white rounded-lg hover:bg-[#5a0b91] flex items-center justify-center gap-2"
                  data-testid="create-snapshot-btn"
                >
                  <Save className={`w-4 h-4 ${creatingSnapshot ? 'animate-pulse' : ''}`} />
                  {lang === 'fr' ? 'Sauvegarder l\'état actuel' : 'Huidige staat opslaan'}
                </button>
              </div>
              <p className="text-xs text-[#1D1D1D]/50 mt-2">
                {lang === 'fr' 
                  ? 'Un snapshot capture les métriques actuelles pour pouvoir les comparer plus tard avec un état futur.'
                  : 'Een snapshot legt de huidige statistieken vast zodat je ze later kunt vergelijken met een toekomstige staat.'}
              </p>
            </div>

            {/* Snapshots List */}
            <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] mb-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#A4187A]" />
                {lang === 'fr' ? 'Snapshots Sauvegardés' : 'Opgeslagen Snapshots'}
                <span className="text-sm font-normal text-[#1D1D1D]/50">({snapshots.length})</span>
              </h3>
              
              {snapshots.length === 0 ? (
                <div className="text-center py-8 text-[#1D1D1D]/50">
                  <GitCompare className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>{lang === 'fr' ? 'Aucun snapshot sauvegardé' : 'Geen opgeslagen snapshots'}</p>
                  <p className="text-sm">{lang === 'fr' ? 'Créez un snapshot pour commencer à suivre les améliorations' : 'Maak een snapshot om verbeteringen te volgen'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {snapshots.map((snapshot) => (
                    <div 
                      key={snapshot.id} 
                      className={`p-4 rounded-lg border ${selectedSnapshot === snapshot.id ? 'border-[#A4187A] bg-purple-50' : 'border-gray-200 hover:border-[#A4187A]/50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{snapshot.name}</h4>
                          <p className="text-xs text-[#1D1D1D]/50">
                            {new Date(snapshot.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'nl-NL', { 
                              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                            })}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right text-sm">
                            <p><span className="text-[#A4187A] font-medium">{snapshot.average_score}%</span> score</p>
                            <p><span className="font-medium">{snapshot.total_questions}</span> questions</p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => compareWithCurrent(snapshot.id)}
                              disabled={comparingSnapshot}
                              className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 ${
                                selectedSnapshot === snapshot.id 
                                  ? 'bg-[#A4187A] text-white' 
                                  : 'bg-gray-100 hover:bg-gray-200'
                              }`}
                              data-testid={`compare-snapshot-${snapshot.id}`}
                            >
                              <GitCompare className="w-4 h-4" />
                              {lang === 'fr' ? 'Comparer' : 'Vergelijken'}
                            </button>
                            <button
                              onClick={() => deleteSnapshot(snapshot.id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                              data-testid={`delete-snapshot-${snapshot.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {snapshot.description && (
                        <p className="text-sm text-[#1D1D1D]/60 mt-2">{snapshot.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Comparison Results */}
            {comparison && (
              <div className="space-y-6">
                {/* Improvement Summary */}
                {comparison.improvements_summary && comparison.improvements_summary.length > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2 text-green-700">
                      <TrendingUp className="w-5 h-5" />
                      {lang === 'fr' ? 'Résumé des Améliorations' : 'Samenvatting Verbeteringen'}
                    </h3>
                    <div className="space-y-2">
                      {comparison.improvements_summary.map((item, idx) => (
                        <div key={idx} className={`flex items-center gap-2 ${
                          item.type === 'positive' ? 'text-green-700' : 'text-blue-700'
                        }`}>
                          {item.type === 'positive' ? <CheckCircle className="w-4 h-4" /> : <Info className="w-4 h-4" />}
                          <span>{lang === 'fr' ? item.message_fr : item.message_nl}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comparison Header */}
                <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-xs text-red-600 font-medium">{lang === 'fr' ? 'AVANT' : 'VOOR'}</p>
                      <p className="font-bold">{comparison.before?.name}</p>
                      <p className="text-xs text-gray-500">{comparison.before?.created_at ? new Date(comparison.before.created_at).toLocaleDateString() : ''}</p>
                    </div>
                    <ArrowRight className="w-8 h-8 text-[#A4187A]" />
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-xs text-green-600 font-medium">{lang === 'fr' ? 'APRÈS' : 'NA'}</p>
                      <p className="font-bold">{comparison.after?.name}</p>
                      <p className="text-xs text-gray-500">{comparison.after?.created_at ? new Date(comparison.after.created_at).toLocaleDateString() : ''}</p>
                    </div>
                  </div>

                  {/* Overall Score */}
                  <div className="text-center py-4 border-t">
                    <p className="text-sm text-[#1D1D1D]/60">{lang === 'fr' ? 'Score d\'amélioration global' : 'Globale verbeteringsscore'}</p>
                    <p className="text-4xl font-bold text-[#A4187A]">{comparison.overall_improvement_score}%</p>
                  </div>
                </div>

                {/* Metrics Comparison */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Accessibility Metrics */}
                  <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                    <h4 className="font-bold mb-4">{lang === 'fr' ? 'Score d\'Accessibilité' : 'Toegankelijkheidsscore'}</h4>
                    <div className="space-y-3">
                      {[
                        { key: 'average_score', label: lang === 'fr' ? 'Score moyen' : 'Gemiddelde score', suffix: '%' },
                        { key: 'highly_accessible', label: lang === 'fr' ? 'Très accessible' : 'Zeer toegankelijk' },
                        { key: 'moderately_accessible', label: lang === 'fr' ? 'Modéré' : 'Matig' },
                        { key: 'low_accessibility', label: lang === 'fr' ? 'Faible' : 'Laag', invertColors: true },
                        { key: 'critical', label: lang === 'fr' ? 'Critique' : 'Kritiek', invertColors: true }
                      ].map(({ key, label, suffix = '', invertColors }) => {
                        const data = comparison.accessibility?.[key];
                        if (!data) return null;
                        const improved = invertColors ? data.delta <= 0 : data.delta > 0;
                        return (
                          <div key={key} className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm">{label}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-sm text-gray-500">{data.before}{suffix}</span>
                              <ArrowRight className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">{data.after}{suffix}</span>
                              <span className={`flex items-center gap-1 text-sm font-medium ${
                                improved ? 'text-green-600' : data.delta === 0 ? 'text-gray-500' : 'text-red-600'
                              }`}>
                                {improved ? <TrendingUp className="w-3 h-3" /> : 
                                 data.delta === 0 ? <Minus className="w-3 h-3" /> : 
                                 <TrendingDown className="w-3 h-3" />}
                                {data.delta > 0 ? '+' : ''}{data.delta}{suffix}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Questions & Coverage */}
                  <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                    <h4 className="font-bold mb-4">{lang === 'fr' ? 'Questions & Couverture' : 'Vragen & Dekking'}</h4>
                    <div className="space-y-3">
                      {comparison.questions?.total_questions && (
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <span className="text-sm">{lang === 'fr' ? 'Nombre de questions' : 'Aantal vragen'}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500">{comparison.questions.total_questions.before}</span>
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{comparison.questions.total_questions.after}</span>
                            <span className={`flex items-center gap-1 text-sm font-medium ${
                              comparison.questions.total_questions.delta > 0 ? 'text-green-600' : 'text-gray-500'
                            }`}>
                              {comparison.questions.total_questions.delta > 0 && <TrendingUp className="w-3 h-3" />}
                              {comparison.questions.total_questions.delta > 0 ? '+' : ''}{comparison.questions.total_questions.delta}
                            </span>
                          </div>
                        </div>
                      )}
                      {comparison.suggestion_coverage?.coverage_rate && (
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <span className="text-sm">{lang === 'fr' ? 'Taux de couverture' : 'Dekkingsgraad'}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500">{comparison.suggestion_coverage.coverage_rate.before}%</span>
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{comparison.suggestion_coverage.coverage_rate.after}%</span>
                            <span className={`flex items-center gap-1 text-sm font-medium ${
                              comparison.suggestion_coverage.coverage_rate.delta > 0 ? 'text-green-600' : 'text-gray-500'
                            }`}>
                              {comparison.suggestion_coverage.coverage_rate.delta > 0 && <TrendingUp className="w-3 h-3" />}
                              {comparison.suggestion_coverage.coverage_rate.delta > 0 ? '+' : ''}{comparison.suggestion_coverage.coverage_rate.delta}%
                            </span>
                          </div>
                        </div>
                      )}
                      {comparison.suggestion_coverage?.never_suggested_count && (
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <span className="text-sm">{lang === 'fr' ? 'Métiers jamais suggérés' : 'Nooit voorgesteld'}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500">{comparison.suggestion_coverage.never_suggested_count.before}</span>
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{comparison.suggestion_coverage.never_suggested_count.after}</span>
                            <span className={`flex items-center gap-1 text-sm font-medium ${
                              comparison.suggestion_coverage.never_suggested_count.delta < 0 ? 'text-green-600' : 'text-gray-500'
                            }`}>
                              {comparison.suggestion_coverage.never_suggested_count.delta < 0 && <TrendingDown className="w-3 h-3" />}
                              {comparison.suggestion_coverage.never_suggested_count.delta}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Domain Scores Comparison Chart */}
                {comparison.domain_scores_comparison && comparison.domain_scores_comparison.length > 0 && (
                  <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                    <h4 className="font-bold mb-4">{lang === 'fr' ? 'Évolution par Domaine' : 'Evolutie per Domein'}</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={comparison.domain_scores_comparison} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} />
                        <YAxis dataKey="domain" type="category" width={100} fontSize={12} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="before" fill="#F44336" name={lang === 'fr' ? 'Avant' : 'Voor'} />
                        <Bar dataKey="after" fill="#4CAF50" name={lang === 'fr' ? 'Après' : 'Na'} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* PART 3: Question Impact */}
        {activeTab === 'questions' && questionImpact && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {['passion', 'talent', 'mission', 'profession'].map(cat => {
                const catQuestions = questionImpact.questions?.filter(q => q.category === cat) || [];
                const totalResponses = catQuestions.reduce((sum, q) => sum + (q.total_responses || 0), 0);
                return (
                  <div key={cat} className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                    <h3 className="font-medium capitalize text-[#A4187A]">{cat}</h3>
                    <p className="text-2xl font-bold">{catQuestions.length} questions</p>
                    <p className="text-xs text-[#1D1D1D]/60">{totalResponses} réponses</p>
                  </div>
                );
              })}
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold mb-4">{lang === 'fr' ? 'Analyse par question' : 'Analyse per vraag'}</h2>
              
              <div className="space-y-4">
                {(questionImpact.questions || []).map((q, idx) => (
                  <div key={idx} className="border border-[#E0E0E0] rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className={`text-xs px-2 py-0.5 rounded mr-2 ${
                          q.category === 'passion' ? 'bg-pink-100 text-pink-700' :
                          q.category === 'talent' ? 'bg-blue-100 text-blue-700' :
                          q.category === 'mission' ? 'bg-green-100 text-green-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {q.category_label}
                        </span>
                        <span className="text-sm font-medium">Q{q.id}: {q.question}</span>
                      </div>
                      <span className="text-xs text-[#1D1D1D]/50">{q.total_responses || 0} réponses</span>
                    </div>
                    
                    {/* Domain Distribution */}
                    <div className="mb-3">
                      <p className="text-xs text-[#1D1D1D]/60 mb-1">{lang === 'fr' ? 'Domaines couverts:' : 'Gedekte domeinen:'} {q.domains_covered_count}/{q.total_domains} ({q.coverage_rate}%)</p>
                      <div className="flex flex-wrap gap-1">
                        {q.domain_distribution?.slice(0, 6).map((d, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                            {d.label}: {d.weight}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Answer Distribution */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {q.answers?.map((a, i) => (
                        <div key={i} className="bg-gray-50 rounded p-2 text-xs">
                          <p className="truncate mb-1">{a.text}</p>
                          <div className="flex justify-between text-[#1D1D1D]/60">
                            <span>{a.domain_labels?.join(', ')}</span>
                            <span className="font-medium">{a.actual_percentage || 0}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* PART 3: Bias Detection */}
        {activeTab === 'bias' && biasDetection && (
          <>
            {/* Score Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-gradient-to-r from-[#A4187A] to-[#9C27B0] rounded-xl p-6 text-white">
                <h3 className="text-sm opacity-80">{lang === 'fr' ? 'Score Global' : 'Globale Score'}</h3>
                <p className="text-4xl font-bold">{biasDetection.bias_score}%</p>
                <p className="text-xs opacity-70">0% = biaisé, 100% = équilibré</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h3 className="text-sm text-[#1D1D1D]/60">{lang === 'fr' ? 'Couverture métiers' : 'Beroepdekking'}</h3>
                <p className="text-4xl font-bold text-[#4CAF50]">{biasDetection.coverage_rate}%</p>
                <p className="text-xs text-[#1D1D1D]/50">{biasDetection.careers_suggested_at_least_once}/{biasDetection.total_careers}</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h3 className="text-sm text-[#1D1D1D]/60">{lang === 'fr' ? 'Équilibre domaines' : 'Domeinbalans'}</h3>
                <p className="text-4xl font-bold text-[#2196F3]">{biasDetection.domain_balance_score}%</p>
              </div>
            </div>
            
            {/* Alerts */}
            {biasDetection.alerts?.length > 0 && (
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] mb-4">
                <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-[#FB6823]" />
                  {lang === 'fr' ? 'Alertes' : 'Waarschuwingen'}
                </h2>
                <div className="space-y-2">
                  {biasDetection.alerts.map((alert, idx) => (
                    <div key={idx} className={`p-3 rounded-lg flex items-start gap-3 ${
                      alert.type === 'critical' ? 'bg-red-50 border-l-4 border-red-500' :
                      alert.type === 'warning' ? 'bg-orange-50 border-l-4 border-orange-500' :
                      'bg-blue-50 border-l-4 border-blue-500'
                    }`}>
                      {alert.type === 'critical' ? <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" /> :
                       alert.type === 'warning' ? <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0" /> :
                       <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />}
                      <div>
                        <p className="font-medium text-sm">{lang === 'fr' ? alert.message : alert.message_nl}</p>
                        <p className="text-xs text-[#1D1D1D]/60 mt-1">{alert.details?.join(', ')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Domain Distribution */}
            <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold mb-4">{lang === 'fr' ? 'Distribution des domaines' : 'Domeinverdeling'}</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={biasDetection.domain_distribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="percentage" fill="#A4187A" radius={[4, 4, 0, 0]}>
                    {biasDetection.domain_distribution?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Never Suggested Careers */}
            {biasDetection.never_suggested?.length > 0 && (
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold mb-3 text-red-600">
                  {lang === 'fr' ? `${biasDetection.never_suggested.length} métiers jamais suggérés` : `${biasDetection.never_suggested.length} nooit voorgestelde beroepen`}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {biasDetection.never_suggested.slice(0, 20).map((c, idx) => (
                    <div key={idx} className="bg-red-50 rounded p-2 text-sm">
                      <p className="font-medium truncate">{c.name}</p>
                      <p className="text-xs text-red-600">{c.domains?.join(', ')}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* PART 4: Missing Links */}
        {activeTab === 'links' && missingLinks && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                <h3 className="text-sm text-[#1D1D1D]/60">{lang === 'fr' ? 'Métiers faiblement connectés' : 'Zwak verbonden beroepen'}</h3>
                <p className="text-3xl font-bold text-orange-600">{missingLinks.total_weakly_connected}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                <h3 className="text-sm text-[#1D1D1D]/60">{lang === 'fr' ? 'Sans formations' : 'Zonder opleidingen'}</h3>
                <p className="text-3xl font-bold text-red-600">{missingLinks.total_without_formations}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                <h3 className="text-sm text-[#1D1D1D]/60">{lang === 'fr' ? 'Domaines sous-servis' : 'Onderbedeelde domeinen'}</h3>
                <p className="text-3xl font-bold text-purple-600">{missingLinks.underserved_domains?.length || 0}</p>
              </div>
            </div>
            
            {/* Domain Coverage */}
            <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] mb-4">
              <h2 className="text-lg font-bold mb-3">{lang === 'fr' ? 'Couverture par domaine' : 'Dekking per domein'}</h2>
              <div className="space-y-2">
                {missingLinks.domain_coverage?.map((d, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <span className="w-24 text-sm font-medium">{d.label}</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${d.is_underserved ? 'bg-red-400' : 'bg-green-400'}`}
                        style={{ width: `${Math.min(d.coverage_ratio, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm text-[#1D1D1D]/60">{d.questions_count} Q / {d.careers_count} métiers</span>
                    {d.is_underserved && <AlertCircle className="w-4 h-4 text-red-500" />}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Questions Missing Domains */}
            {missingLinks.questions_missing_domains?.length > 0 && (
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                <h2 className="text-lg font-bold mb-3">{lang === 'fr' ? 'Questions à améliorer' : 'Te verbeteren vragen'}</h2>
                <div className="space-y-2">
                  {missingLinks.questions_missing_domains.map((q, idx) => (
                    <div key={idx} className="bg-yellow-50 rounded p-3">
                      <p className="font-medium text-sm">Q{q.question_id}: {q.question}</p>
                      <p className="text-xs text-[#1D1D1D]/60 mt-1">
                        {lang === 'fr' ? 'Domaines manquants:' : 'Ontbrekende domeinen:'} {q.missing_domains.join(', ')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* PART 5: Education Data */}
        {activeTab === 'education' && educationData && (
          <>
            <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1D1D1D]/40" />
                  <input
                    type="text"
                    placeholder={lang === 'fr' ? 'Rechercher un métier...' : 'Zoek een beroep...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-[#E0E0E0]"
                  />
                </div>
                <span className="text-sm text-[#1D1D1D]/60">{educationData.total} métiers</span>
              </div>
              
              <div className="space-y-3">
                {(educationData.careers || [])
                  .filter(c => !searchTerm || c.career_name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .slice(0, 30)
                  .map((career, idx) => (
                    <div key={idx} className="border border-[#E0E0E0] rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-medium">{career.career_name}</h3>
                          <p className="text-sm text-[#1D1D1D]/60">{career.typical_education}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          career.study_level === 'Doctorat' ? 'bg-purple-100 text-purple-700' :
                          career.study_level === 'Master (Bac+5)' ? 'bg-blue-100 text-blue-700' :
                          career.study_level === 'Bachelier (Bac+3/4)' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {career.study_level}
                        </span>
                      </div>
                      
                      {/* Region Availability */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {career.available_in_wallonia && <span className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded flex items-center gap-1"><MapPin className="w-3 h-3" />Wallonie</span>}
                        {career.available_in_flanders && <span className="text-xs px-2 py-1 bg-yellow-50 text-yellow-700 rounded flex items-center gap-1"><MapPin className="w-3 h-3" />Flandre</span>}
                        {career.available_in_brussels_fr && <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded flex items-center gap-1"><MapPin className="w-3 h-3" />Bruxelles FR</span>}
                        {career.available_in_brussels_nl && <span className="text-xs px-2 py-1 bg-orange-50 text-orange-700 rounded flex items-center gap-1"><MapPin className="w-3 h-3" />Brussel NL</span>}
                        {!career.available_in_wallonia && !career.available_in_flanders && !career.available_in_brussels_fr && !career.available_in_brussels_nl && (
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">{lang === 'fr' ? 'Aucune formation liée' : 'Geen gekoppelde opleiding'}</span>
                        )}
                      </div>
                      
                      {/* Formations */}
                      {career.formations_count > 0 && (
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs font-medium mb-2">{career.formations_count} {lang === 'fr' ? 'formations disponibles' : 'beschikbare opleidingen'}</p>
                          <div className="space-y-1">
                            {career.formations?.slice(0, 3).map((f, i) => (
                              <div key={i} className="flex justify-between items-center text-xs">
                                <span>{f.name} - {f.institution}</span>
                                <span className="text-[#1D1D1D]/50">{f.type_label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}

        {/* PART 6: Visualization */}
        {activeTab === 'visualization' && visualization && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                <p className="text-2xl font-bold text-[#A4187A]">{visualization.summary?.total_questions}</p>
                <p className="text-xs text-[#1D1D1D]/60">Questions</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                <p className="text-2xl font-bold text-[#FB6823]">{visualization.summary?.total_domains}</p>
                <p className="text-xs text-[#1D1D1D]/60">Domaines</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                <p className="text-2xl font-bold text-[#4CAF50]">{visualization.summary?.total_careers}</p>
                <p className="text-xs text-[#1D1D1D]/60">Métiers</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
                <p className="text-2xl font-bold text-[#2196F3]">{visualization.summary?.total_connections}</p>
                <p className="text-xs text-[#1D1D1D]/60">Connexions</p>
              </div>
            </div>
            
            {/* Heatmap */}
            <div className="bg-white rounded-xl p-4 border border-[#E0E0E0] mb-4">
              <h2 className="text-lg font-bold mb-4">{lang === 'fr' ? 'Heatmap Questions × Domaines' : 'Heatmap Vragen × Domeinen'}</h2>
              <div className="overflow-x-auto">
                <table className="text-xs">
                  <thead>
                    <tr>
                      <th className="p-1 text-left">Question</th>
                      {['health', 'social', 'education', 'creativity', 'technology', 'technical', 'science', 'business', 'administration', 'sports'].map(d => (
                        <th key={d} className="p-1 text-center w-12">{d.slice(0, 4)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visualization.heatmap?.slice(0, 20).map((row, idx) => (
                      <tr key={idx}>
                        <td className="p-1 text-left truncate max-w-[200px]">Q{row.question_id}</td>
                        {['health', 'social', 'education', 'creativity', 'technology', 'technical', 'science', 'business', 'administration', 'sports'].map(d => (
                          <td key={d} className="p-1">
                            <div
                              className="w-8 h-6 rounded"
                              style={{ backgroundColor: `rgba(106, 13, 173, ${Math.min((row[d] || 0) / 4, 1)})` }}
                              title={`${row[d] || 0}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Orphan Careers */}
            {visualization.orphan_count > 0 && (
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <h2 className="text-lg font-bold text-red-700 mb-3">
                  {visualization.orphan_count} {lang === 'fr' ? 'métiers orphelins' : 'weesberepen'}
                </h2>
                <p className="text-sm text-red-600 mb-2">{lang === 'fr' ? 'Ces métiers n\'ont aucune connexion avec les questions' : 'Deze beroepen hebben geen verbinding met de vragen'}</p>
                <div className="flex flex-wrap gap-2">
                  {visualization.orphan_careers?.map((c, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">{c}</span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* PART 7: Recommendations */}
        {activeTab === 'recommendations' && recommendations && (
          <>
            <div className="bg-white rounded-xl p-4 border border-[#E0E0E0]">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-[#FFC107]" />
                {recommendations.total} {lang === 'fr' ? 'recommandations pour améliorer le système' : 'aanbevelingen om het systeem te verbeteren'}
              </h2>
              
              <div className="space-y-4">
                {(recommendations.recommendations || []).map((rec, idx) => (
                  <div
                    key={idx}
                    className={`rounded-lg p-4 border-l-4 ${
                      rec.priority === 'critical' ? 'bg-red-50 border-red-500' :
                      rec.priority === 'high' ? 'bg-orange-50 border-orange-500' :
                      rec.priority === 'medium' ? 'bg-yellow-50 border-yellow-500' :
                      rec.priority === 'low' ? 'bg-blue-50 border-blue-500' :
                      'bg-gray-50 border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            rec.priority === 'critical' ? 'bg-red-200 text-red-800' :
                            rec.priority === 'high' ? 'bg-orange-200 text-orange-800' :
                            rec.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                            rec.priority === 'low' ? 'bg-blue-200 text-blue-800' :
                            'bg-gray-200 text-gray-800'
                          }`}>
                            {rec.priority.toUpperCase()}
                          </span>
                          <span className="text-xs text-[#1D1D1D]/50">{rec.category}</span>
                        </div>
                        <h3 className="font-medium">{lang === 'fr' ? rec.title : rec.title_nl}</h3>
                        <p className="text-sm text-[#1D1D1D]/70 mt-1">{lang === 'fr' ? rec.description : rec.description_nl}</p>
                        {rec.affected_careers && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {rec.affected_careers.slice(0, 5).map((c, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-white rounded border">{c}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-[#1D1D1D]/50">{lang === 'fr' ? 'Métrique' : 'Metriek'}</span>
                        <div className="font-bold text-[#A4187A]">{rec.metric}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

      </main>
      
      {/* Footer */}
      <footer className="bg-[#1D1D1D] text-white py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-white/60">
          Déclic – Module d'Audit & Transparence · {new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
}
